// src/components/modals/ViewProductModal.jsx
import { X, Package, Tag, DollarSign, TrendingUp, Calendar, Info } from 'lucide-react'
import { Button } from '../ui/Button'
import { format } from 'date-fns'

const ViewProductModal = ({ isOpen, onClose, product }) => {
  // Handle modal close
  const handleClose = () => {
    onClose()
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Calculate profit margin
  const calculateProfitMargin = (cost, sellingPrice) => {
    if (!cost || !sellingPrice || cost <= 0) return 0
    return ((sellingPrice - cost) / cost) * 100
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return format(new Date(dateString), 'PPP')
    } catch {
      return 'Invalid date'
    }
  }

  // Get status badge classes
  const getStatusBadgeClasses = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200'
  }

  // Get type badge classes
  const getTypeBadgeClasses = (type) => {
    return type === 'product' 
      ? 'bg-blue-100 text-blue-800'
      : 'bg-orange-100 text-orange-800'
  }

  // Get type icon
  const getTypeIcon = (type) => {
    return type === 'product' ? '📦' : '🍳'
  }

  // Don't render if not open or no product
  if (!isOpen || !product) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              {/* <div className="flex-shrink-0">
                <Package className="h-6 w-6 text-orange-600" />
              </div> */}
              <h3 className="text-lg font-semibold text-gray-900">Product Details</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <Info className="h-4 w-4 mr-2" />
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Product Name
                  </label>
                  <p className="mt-1 text-sm text-gray-900 font-medium">{product.name}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    SKU
                  </label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{product.sku}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Category
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {product.categoryDetails?.name || 'Category not found'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Product Type
                  </label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeClasses(product.type)}`}>
                      {getTypeIcon(product.type)} {product.type === 'product' ? 'Product' : 'Recipe Based'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock & Unit Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <Package className="h-4 w-4 mr-2" />
                Stock & Unit
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Unit
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {product.unitDetails ? 
                      `${product.unitDetails.name} (${product.unitDetails.abbreviation})` : 
                      product.unit ? 'Unit details not available' : 'No unit assigned'
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Threshold
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{product.threshold || 0}</p>
                </div>
              </div>
            </div>

            {/* Pricing Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <DollarSign className="h-4 w-4 mr-2" />
                Pricing
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Cost
                  </label>
                  <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(product.cost)}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Outlet Price
                  </label>
                  <p className="mt-1 text-lg font-semibold text-green-600">{formatCurrency(product.outletPrice)}</p>
                  <p className="text-xs text-gray-500">
                    Margin: {calculateProfitMargin(product.cost, product.outletPrice).toFixed(1)}%
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    PickMe Price
                  </label>
                  <p className="mt-1 text-lg font-semibold text-blue-600">{formatCurrency(product.pickMePrice)}</p>
                  <p className="text-xs text-gray-500">
                    Margin: {calculateProfitMargin(product.cost, product.pickMePrice).toFixed(1)}%
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Uber Price
                  </label>
                  <p className="mt-1 text-lg font-semibold text-purple-600">{formatCurrency(product.uberPrice)}</p>
                  <p className="text-xs text-gray-500">
                    Margin: {calculateProfitMargin(product.cost, product.uberPrice).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            {/* Profit Analysis */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" />
                Profit Analysis
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Outlet Profit</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(product.outletPrice - product.cost)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {calculateProfitMargin(product.cost, product.outletPrice).toFixed(1)}% margin
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">PickMe Profit</p>
                  <p className="text-lg font-bold text-blue-600">
                    {formatCurrency(product.pickMePrice - product.cost)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {calculateProfitMargin(product.cost, product.pickMePrice).toFixed(1)}% margin
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Uber Profit</p>
                  <p className="text-lg font-bold text-purple-600">
                    {formatCurrency(product.uberPrice - product.cost)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {calculateProfitMargin(product.cost, product.uberPrice).toFixed(1)}% margin
                  </p>
                </div>
              </div>
            </div>

            {/* Status & Timestamps */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Status & Timeline
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Status
                  </label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClasses(product.isActive)}`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Created
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(product.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Last Updated
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(product.updatedAt)}</p>
                </div>
              </div>
            </div>

            {/* Product ID for reference */}
            {/* <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">
                <span className="font-medium">Product ID:</span> {product._id}
              </p>
            </div> */}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewProductModal